import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Text, Title, useModalState } from "@src/appV2/redesign/system/ui-components";
import { NoTimesheetBottomSheet } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/components/NoTimesheetBottomSheet";
import { UploadTimesheetBottomSheet } from "@src/appV2/redesign/WorkerShifts/UnverifiedShifts/UploadTimesheet/UploadTimesheetBottomSheet";

interface TimesheetUploadSectionProps {
  onSuccess: () => void;
  onSkip: () => void;
  is100InstantPayEnabled: boolean;
  shiftId: string;
  agentId: string;
  agentName: string;
  facilityId: string;
  isTimesheetUploaded: boolean;
  timesheetExists: boolean;
}

export function TimesheetUploadSection(props: TimesheetUploadSectionProps) {
  const {
    onSuccess,
    onSkip,
    is100InstantPayEnabled,
    shiftId,
    agentId,
    agentName,
    facilityId,
    isTimesheetUploaded,
    timesheetExists,
  } = props;
  const uploadTimesheetBottomSheetModalState = useModalState();
  const noTimesheetBottomSheetModalState = useModalState();

  return (
    <>
      {!timesheetExists && (
        <Stack
          spacing={5}
          sx={{
            paddingX: 6,
            paddingY: 6,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Title bold variant="h6" component="h2">
            Upload a paper timesheet to get paid
          </Title>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            {is100InstantPayEnabled
              ? "Please upload your timesheet to get paid."
              : "Nice work! Your shift is complete. You'll receive the rest of your payment once the shift is verified."}
          </Text>

          <LoadingButton
            size="large"
            isLoading={false}
            onClick={() => {
              uploadTimesheetBottomSheetModalState.openModal();
            }}
          >
            Upload Your Timesheet
          </LoadingButton>

          <Box
            sx={{
              textAlign: "center",
            }}
          >
            <TextButton
              onClick={() => {
                noTimesheetBottomSheetModalState.openModal();
              }}
            >
              I don&#39;t have a timesheet
            </TextButton>
          </Box>
        </Stack>
      )}

      <UploadTimesheetBottomSheet
        modalState={uploadTimesheetBottomSheetModalState}
        shiftId={shiftId}
        isTimesheetUploaded={isTimesheetUploaded}
        onUploadStarted={() => {
          logEvent(APP_V2_USER_EVENTS.PAPER_TIMESHEET_UPLOAD_STARTED, {
            shiftId,
            workerId: agentId,
            workplaceId: facilityId,
          });
        }}
        onSuccess={async ({ uploadDurationInMs, updateTimecardDurationInMs }) => {
          logEvent(APP_V2_USER_EVENTS.PAPER_TIMESHEET_UPLOADED, {
            shiftId,
            workerId: agentId,
            workplaceId: facilityId,
            isReupload: isTimesheetUploaded,
            uploadDurationInMs,
            updateTimecardDurationInMs,
          });
          onSuccess();
        }}
        onError={(error) => {
          logEvent(APP_V2_USER_EVENTS.PAPER_TIMESHEET_UPLOAD_ERROR, {
            shiftId,
            workerId: agentId,
            workplaceId: facilityId,
            error: error?.message ?? "Unknown error",
          });
        }}
      />

      <NoTimesheetBottomSheet
        modalState={noTimesheetBottomSheetModalState}
        shiftId={shiftId}
        agentId={agentId}
        agentName={agentName}
        onContinue={onSkip}
      />
    </>
  );
}
